<template>
  <div class="today-order">
    <ShopScreenTopBar
      :payee-id="payeeId"
      :catalog-id="catalogId"
      screen="handout"
      :now="now"
      :freshness="dataFreshness"
      :pending="pending"
      :show-settings="payeeAdmin"
      @refresh="reloadData"
      @settings="settingsOpen = true"
    />

    <TodayOrderSettingsModal
      v-model="settingsOpen"
      :config="todayOrderConfig"
      @updated="todayOrderConfig = $event"
    />

    <div class="content">
      <div class="payers-column">
        <SearchBox
          ref="search"
          v-model="search"
          lazy
          class="search"
          constant-width
          :placeholder="$t('shop.todayOrder.searchOrScan')"
          @focus="showPayers = true"
          @blur="delayedHide"
        />
        <div
          v-if="!isSmallScreen || showPayers"
          class="payers-list"
        >
          <div
            v-for="p in visiblePayers"
            :key="p.id"
            class="list-payer d-flex justify-content-between align-items-center"
            :class="{ selected: payer && p.id === payer.id }"
            @click="payer = p; showPayers = false"
          >
            <div class="flex-grow-1">
              <PayerName
                v-bind="p"
                :code="undefined"
              />
              <div
                v-if="p.code"
                class="small text-secondary"
              >
                {{ p.code }}
              </div>
            </div>
            <DeliveryBadge :orders="p.orders" />
          </div>

          <InfinityScrollCursor
            ref="cursor"
            @request="showMore"
          />
        </div>
      </div>

      <div
        v-show="!isSmallScreen || !showPayers || error"
        class="details-column"
      >
        <div
          v-if="error"
          class="text-danger text-center"
        >
          <div style="font-size: 45px">
            <i class="fas fa-xmark-large" />
          </div>
          <div
            style="font-size: 20px"
            class="font-weight-bold"
          >
            {{ $t('error.error') }}
          </div>
        </div>
        <TodayPayerOrder
          v-if="payer"
          :key="payer.id"
          :payer="payer"
          :order-data="orders"
          :offer="todayOffer"
          :delivery-plan="deliveryPlan"
          :diets="diets"
          :catalog-id="catalogId"
          :properties="visibleProperties"
          :show-meal-title="todayOrderConfig.showMealTitle"
          :fast-edit="todayOrderConfig.screenVariant === 'FastEdit'"
          :bracelet-property="todayOrderConfig.braceletPayerProperty"
          @bracelet-assigned="request"
          @delivered="delivered"
          @updated="requestOrders"
        />
      </div>
    </div>
  </div>
</template>

<script>
import InfinityScrollCursor from '@/components/InfinityScrollCursor';
import PayerName from '@/components/payers/PayerName';
import TodayPayerOrder from '@/components/shop/TodayPayerOrder';
import ShopScreenTopBar from '@/components/shop/today/ShopScreenTopBar';
import TodayOrderSettingsModal from '@/components/shop/today/TodayOrderSettingsModal';
import { isScanProperty } from '@/utils/payer-properties';
import createSearch from '@/utils/search';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import DeliveryBadge from './delivery/DeliveryBadge';

const breakPoint = 992;

export default {
  props: {
    payeeId: String,
  },
  data: () => ({
    search: '',
    list: [],
    orders: {},
    listings: [],
    todayListingIds: [],
    deliveryPlan: [],
    diets: [],
    payer: null,
    showPayers: false,
    isSmallScreen: true,
    dataFrom: null,
    now: null,
    pending: false,
    error: false,
    take: 20,
    settingsOpen: false,
    todayOrderConfig: {
      visiblePayerProperties: [],
      showMealTitle: true,
      screenVariant: 'Simple',
    },
  }),
  components: {
    DeliveryBadge,
    TodayPayerOrder,
    ShopScreenTopBar,
    TodayOrderSettingsModal,
    InfinityScrollCursor,
    PayerName,
  },
  computed: {
    ...mapGetters(['payee', 'properties', 'currencies', 'payeeAdmin']),
    catalogId() {
      return this.$route.query.catalogId;
    },
    today() {
      return moment(this.now).format('YYYY-MM-DD');
    },
    todayOffer() {
      return this.listings.filter((l) => this.todayListingIds.includes(l.id));
    },
    visiblePayers() {
      return this.filteredPayers.slice(0, this.take);
    },
    filteredPayers() {
      const search = createSearch(this.search);
      const codes = this.additionalProperties.map((p) => p.code);
      return this.payersWithOrders
        .filter((x) =>
          search(x.name)
         || search(x.code)
         || codes.some((c) => x.properties && search(x.properties[c])));
    },
    payersWithOrders() {
      return this.list
        .map((x) => ({
          ...x,
          orders: this.getPayerOrders(x),
        }));
    },
    dataFreshness() {
      if (!this.dataFrom) return '';
      const seconds = moment(this.now).unix() - moment(this.dataFrom).unix();
      if (seconds < 0) {
        return 0;
      }

      return seconds / 60;
    },
    // Properties used only to find the payer: scanned codes and the bracelet
    additionalProperties() {
      return this.properties
        .filter((p) => isScanProperty(p) || p.code === this.todayOrderConfig.braceletPayerProperty);
    },
    visibleProperties() {
      const visible = this.todayOrderConfig.visiblePayerProperties || [];
      const codes = this.additionalProperties.map((p) => p.code);
      return this.properties
        .filter((p) => !codes.includes(p.code) && visible.includes(p.code));
    },
  },
  watch: {
    search() {
      this.take = 20;
    },
  },
  methods: {
    ...mapActions('shop', [
      'getOrders',
      'getCatalogListings',
      'getListingsCalendar',
      'getShopCatalog',
      'getDeliveryPlanCalendar',
      'getTodayOrderConfig',
    ]),
    ...mapActions([
      'getPayers',
      'getPayersProperties',
      'getPayee',
      'getCurrencies',
      'getUserPermissions',
    ]),
    showMore() {
      this.take += 10;
    },
    getPayerOrders({ id }) {
      return this.orders?.orders?.filter((x) => x.payerId === id) || [];
    },
    delivered(payerId) {
      // Delivery is registered for all payer orders of the day
      this.orders.orders
        .filter((x) => x.payerId === payerId)
        .forEach((o) => {
          const order = o;
          order.delivered += 1;
        });
    },
    delayedHide() {
      // Delay blur, to handle click
      if (this.payer) {
        setTimeout(() => { this.showPayers = false; }, 100);
      }
    },
    selectFirst(search) {
      this.search = search;
      this.$nextTick(() => {
        if (this.filteredPayers.length > 0) {
          [this.payer] = this.filteredPayers;
        }
        this.search = '';
        this.$refs.search.set('');
        this.$refs.search.blur();
        this.showPayers = false;
      });
    },
    requestOrders() {
      this.error = false;
      this.pending = true;

      this.getOrders({
        params: {
          query: {
            from: moment().format('YYYY-MM-DD'),
            to: moment().add(1, 'day').format('YYYY-MM-DD'),
            catalogId: this.catalogId,
          },
        },
      })
        .then(({ data }) => {
          this.orders = data;
          this.dataFrom = moment().format();
          this.pending = false;
        })
        .catch(() => {
          this.pending = false;
          this.error = true;
        });
    },
    requestOffer() {
      if (!this.catalogId) return;

      this.getCatalogListings({
        params: {
          catalogId: this.catalogId,
        },
      })
        .then(({ data }) => {
          this.listings = data;
        });

      const today = moment().format('YYYY-MM-DD');
      this.getListingsCalendar({
        params: {
          catalogId: this.catalogId,
          query: {
            from: today,
            to: moment().add(1, 'day').format('YYYY-MM-DD'),
          },
        },
      })
        .then(({ data }) => {
          this.todayListingIds = data
            .filter((x) => x.day === today)
            .map((x) => x.listingId);
        });
    },
    // Menu texts for today, for every menu of the catalog
    requestDeliveryPlan() {
      if (!this.catalogId) return;

      const from = moment().format('YYYY-MM-DD');
      const to = moment().add(1, 'day').format('YYYY-MM-DD');

      this.getShopCatalog({
        params: {
          catalogId: this.catalogId,
        },
      })
        .then(({ data }) => {
          const menuSubCatalogId = data.metadata?.menuId;
          this.diets = (data.subCatalogNodes || [])
            .filter((n) => n.subCatalogId === data.metadata?.dietId);
          if (!menuSubCatalogId) return [];

          const menuNodes = (data.subCatalogNodes || [])
            .filter((n) => n.subCatalogId === menuSubCatalogId);

          return Promise.all(menuNodes.map((n) => this.getDeliveryPlanCalendar({
            params: {
              catalogId: this.catalogId,
              menuNodeId: n.nodeId,
              query: { from, to },
            },
          })
            .then(({ data: entries }) => entries.map((e) => ({ ...e, menuId: n.nodeId })))));
        })
        .then((plans) => {
          this.deliveryPlan = plans.flat();
        })
        .catch(() => {
          this.deliveryPlan = [];
        });
    },
    request() {
      return this.getPayers({
        params: {
          query: {
            skip: 0,
            take: -1,
            withProperty: this.properties.map((x) => x.code),
          },
        },
      })
        .then(({ data }) => {
          this.list = data;
          // Keep the selected payer up to date, e.g. after assigning a bracelet
          if (this.payer) {
            this.payer = data.find((x) => x.id === this.payer.id) || this.payer;
          }
          return data;
        });
    },
    requestConfig() {
      this.getTodayOrderConfig()
        .then(({ data }) => {
          this.todayOrderConfig = data;
        })
        .catch(() => {
          // Keep the defaults, the screen works without the config
        });
    },
    reloadData() {
      this.requestConfig();
      this.requestOrders();
      this.requestOffer();
      this.requestDeliveryPlan();
      this.getPayersProperties()
        .then(() => {
          this.request();
        });
    },
  },
  created() {
    this.$store.commit('setPayeeId', this.payeeId);
    this.requestConfig();
    this.requestOrders();
    this.requestOffer();
    this.requestDeliveryPlan();

    if (!this.currencies || this.currencies.length === 0) {
      this.getCurrencies();
    }

    // The page is outside the payee layout, which loads the payee and the permissions.
    // The payee is needed to recognize the tenant admin (payee.ownerId).
    this.getPayee({
      params: {
        payeeId: this.payeeId,
      },
    });
    this.getUserPermissions();

    this.refreshInterval = setInterval(() => {
      this.requestOrders();
    }, 10000);

    this.getPayersProperties()
      .then(() => {
        this.request();
      });

    this.updateIsSmallScreen = () => {
      this.isSmallScreen = window.innerWidth < breakPoint;
    };

    this.keyDown = (e) => {
      if (this.settingsOpen) return;

      const text = this.$refs.search.search;

      if (e.keyCode === 13) {
        setTimeout(() => {
          this.selectFirst(text);
        }, 50);
      } else if (!this.showPayers) {
        const isChar = typeof e.which === 'number'
          && e.which > 0
          && !e.ctrlKey
          && !e.metaKey
          && !e.altKey
          && e.which !== 8;

        if (e.keyCode === 8) {
          this.$refs.search.set(text.substring(0, text.length - 1));
        } else if (e.keyCode !== 13 && isChar) {
          this.$refs.search.set(`${text}${e.key}`);
        }
      }
    };

    this.now = moment().format();
    this.nowInterval = setInterval(() => {
      this.now = moment().format();
    }, 5000);

    // The app is desktop-only by default (fixed 1280px viewport), this page supports phones
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      this.initialViewport = viewport.getAttribute('content');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
    }
    document.documentElement.classList.add('responsive-page');

    this.updateIsSmallScreen();
    window.addEventListener('resize', this.updateIsSmallScreen);
    window.addEventListener('keydown', this.keyDown);
  },
  destroyed() {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport && this.initialViewport) {
      viewport.setAttribute('content', this.initialViewport);
    }
    document.documentElement.classList.remove('responsive-page');

    window.removeEventListener('resize', this.updateIsSmallScreen);
    window.removeEventListener('keydown', this.keyDown);
    if (this.nowInterval) {
      clearInterval(this.nowInterval);
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  mounted() {
    if (this.$refs.search) {
      this.$refs.search.focus();
    }
  },
};
</script>

<style lang="scss" scoped>
.today-order {
  min-height: 100vh;
  background-color: #efefef;
}

.search {
  ::v-deep .search-input {
    background-color: white;
    border-radius: 8px;
  }
}

.list-payer {
  padding: 10px 15px;
  background-color: #fff;
  border-radius: 10px;
  margin-top: 6px;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    border: 2px solid rgba($blue, 0.2);
  }

  &.selected {
    background-color: #c3d8f5;
  }
}

// Phone / tablet portrait: one column
.payers-column {
  padding: 0 16px 12px;
  border-bottom: 1px solid #ddd;
}

.payers-list {
  margin-top: 12px;
  padding-bottom: 8px;
}

.details-column {
  background-color: #fafafa;
  padding: 16px;
  min-height: calc(100vh - 120px);
}

// Large screens: list on the left, details on the right
@media screen and (min-width: 992px) {
  .today-order {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .content {
    display: flex;
    flex: 1;
    min-height: 0;
    gap: 30px;
    padding: 16px 20px 0 20px;
  }

  .payers-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px 0 0;
    border-bottom: none;
    min-height: 0;
  }

  .search {
    ::v-deep .search-input {
      background-color: #f4f4f4;
    }
  }

  .payers-list {
    flex: 1;
    overflow-y: auto;
    padding-right: 5px;
  }

  .details-column {
    flex: 1;
    overflow-y: auto;
    border-radius: 10px 10px 0 0;
    padding: 30px 30px 0;
    min-height: 0;
  }
}
</style>
